import { useCallback, useRef, useState } from "react";
import type { CSSProperties, PointerEvent, ReactNode } from "react";

interface Card3DProps {
  children: ReactNode;
  /** Max rotation in degrees at the card's corners. 0 disables the tilt. */
  intensity?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * A panel that tilts toward the pointer.
 *
 * The transform lives entirely in CSS (see .tilt in styles/global.css); this
 * component only writes the custom properties it reads. Values are set through
 * the raw style object rather than React state so the surface tracks the
 * pointer without a re-render per mousemove.
 */
export default function Card3D({
  children,
  intensity = 6,
  className = "",
  style,
}: Card3DProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [live, setLive] = useState(false);
  const frame = useRef(0);

  const onMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!intensity) return;
      const el = ref.current;
      if (!el) return;

      const { clientX, clientY } = e;
      // one write per frame; pointermove can fire far faster than paint
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const px = (clientX - r.left) / r.width; // 0..1 across
        const py = (clientY - r.top) / r.height; // 0..1 down
        // pointer above centre tips the top edge away from the viewer
        el.style.setProperty("--ry", `${(px - 0.5) * 2 * intensity}deg`);
        el.style.setProperty("--rx", `${(0.5 - py) * 2 * intensity}deg`);
        el.style.setProperty("--mx", `${px * 100}%`);
        el.style.setProperty("--my", `${py * 100}%`);
      });
    },
    [intensity],
  );

  const reset = useCallback(() => {
    cancelAnimationFrame(frame.current);
    const el = ref.current;
    if (!el) return;
    setLive(false);
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  }, []);

  return (
    <div className="tilt-stage" style={style}>
      <div
        ref={ref}
        className={`panel3d tilt ${live ? "is-live" : ""} ${className}`}
        onPointerEnter={() => intensity > 0 && setLive(true)}
        onPointerMove={onMove}
        onPointerLeave={reset}
      >
        {children}
      </div>
    </div>
  );
}
